import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'

type Channel = 'boost4life.ro' | 'eMAG'
type Status = 'draft' | 'ready' | 'files_generated' | 'active' | 'reverted' | 'cancelled'

interface ProductsConfigItem {
  productId: number
  discountPct: number
}

interface PromoCampaign {
  id: number
  name: string
  startDate: string
  endDate: string
  channels: Channel[]
  productsConfig: ProductsConfigItem[]
  pAndLImpact: PAndLImpact | null
  csvShopifyPath: string | null
  csvRevertPath: string | null
  xlsEmagPath: string | null
  status: Status
  createdAt: string
  updatedAt: string
}

interface ChannelImpact {
  channel: Channel
  basePrice: number
  salePrice: number
  baseNett: number
  saleNett: number
  cost: number | null
  baseMargin: number | null
  saleMargin: number | null
  baseMarginPct: number | null
  saleMarginPct: number | null
  marginLossPct: number | null
}

interface ProductImpact {
  productId: number
  sku: string
  name: string
  brand: string
  costProduction: number | null
  vatRate: number
  discountPct: number
  channels: ChannelImpact[]
}

interface PAndLImpact {
  totalProducts: number
  byProduct: ProductImpact[]
  summary: {
    avgDiscountPct: number
    totalMarginErosionPerUnit: number
  }
}

interface Product {
  id: number
  sku: string
  name: string
  brand: string
  active: boolean
}

const STATUS_LABELS: Record<Status, string> = {
  draft: 'Draft',
  ready: 'Calculat P&L',
  files_generated: 'Fișiere generate',
  active: 'Activă',
  reverted: 'Prețuri revenite',
  cancelled: 'Anulată',
}

const STATUS_COLORS: Record<Status, string> = {
  draft: 'bg-b4l-bg text-b4l-text-muted',
  ready: 'bg-b4l-blue-light text-b4l-blue-dark',
  files_generated: 'bg-b4l-amber-light text-b4l-amber-dark',
  active: 'bg-b4l-green-light text-b4l-green-dark',
  reverted: 'bg-b4l-purple-light text-b4l-purple-dark',
  cancelled: 'bg-b4l-red-light text-b4l-red-dark',
}

export function CampaniiPromoFluxPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.promo:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [wizardId, setWizardId] = useState<number | null>(null)

  const query = useQuery<{ campaigns: PromoCampaign[] }>({
    queryKey: ['promo-campaigns'],
    queryFn: async () =>
      (await api.get<{ campaigns: PromoCampaign[] }>('/marketing/promo-campaigns')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: {
      name: string
      startDate: string
      endDate: string
      channels: Channel[]
      productsConfig: ProductsConfigItem[]
    }) => (await api.post<{ campaign: PromoCampaign }>('/marketing/promo-campaigns', data)).data,
    onSuccess: ({ campaign }) => {
      queryClient.invalidateQueries({ queryKey: ['promo-campaigns'] })
      toast.success('Campanie creată')
      setCreateOpen(false)
      setWizardId(campaign.id)
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/promo-campaigns/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-campaigns'] })
      toast.success('Șters')
    },
    onError: showError,
  })

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: Status }) =>
      (await api.patch(`/marketing/promo-campaigns/${id}/status`, { status })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-campaigns'] })
    },
    onError: showError,
  })

  const campaigns = useMemo(() => query.data?.campaigns ?? [], [query.data])
  const pag = useClientPagination(campaigns, 10)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Campanii promo - flux complet</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Campanie nouă</Button>}
      </div>

      <Card description="Wizard în 4 pași: detalii → produse cu discount → calcul P&L → generare fișiere export Shopify (CSV) și eMAG (XLSX). Click pe „Continuă wizard” pentru a deschide flux complet.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : campaigns.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Nicio campanie. {canWrite && 'Apasă „+ Campanie nouă”.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">Denumire</th>
                  <th className="py-2 pr-3 font-medium">Perioadă</th>
                  <th className="py-2 pr-3 font-medium">Canale</th>
                  <th className="py-2 pr-3 font-medium text-right">Produse</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((c) => (
                  <tr key={c.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 font-medium">{c.name}</td>
                    <td className="py-2 pr-3 text-xs">
                      {new Date(c.startDate).toLocaleDateString('en-GB')}
                      {' - '}
                      {new Date(c.endDate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-2 pr-3 text-xs">{(c.channels || []).join(', ') || '—'}</td>
                    <td className="py-2 pr-3 text-right text-xs">
                      {(c.productsConfig || []).length}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status]}`}
                      >
                        {STATUS_LABELS[c.status]}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          variant="secondary"
                          onClick={() => setWizardId(c.id)}
                          className="!px-2 !py-1 !text-xs"
                        >
                          {c.status === 'draft' ? 'Continuă wizard' : 'Deschide'}
                        </Button>
                        {canWrite && c.status === 'files_generated' && (
                          <Button
                            onClick={() => statusMutation.mutate({ id: c.id, status: 'active' })}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Marchează activă
                          </Button>
                        )}
                        {canWrite && c.status === 'active' && (
                          <Button
                            variant="secondary"
                            onClick={() => statusMutation.mutate({ id: c.id, status: 'reverted' })}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Marchează prețurile ca revenite
                          </Button>
                        )}
                        {canWrite && (c.status === 'draft' || c.status === 'cancelled') && (
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi „${c.name}”?`)) deleteMutation.mutate(c.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="campanii"
          />
        </div>
      </Card>

      {createOpen && (
        <CreateCampaignModal
          submitting={createMutation.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
        />
      )}

      {wizardId != null && (
        <WizardModal campaignId={wizardId} canWrite={canWrite} onClose={() => setWizardId(null)} />
      )}
    </div>
  )
}

// ============================================================================
// Create modal (Step 1 inputs only — name, period, channels)
// ============================================================================

function CreateCampaignModal({
  submitting,
  onClose,
  onSubmit,
}: {
  submitting: boolean
  onClose: () => void
  onSubmit: (data: {
    name: string
    startDate: string
    endDate: string
    channels: Channel[]
    productsConfig: ProductsConfigItem[]
  }) => void
}) {
  const [name, setName] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [channels, setChannels] = useState<Channel[]>(['boost4life.ro'])

  const toggle = (ch: Channel) => {
    setChannels((prev) => (prev.includes(ch) ? prev.filter((x) => x !== ch) : [...prev, ch]))
  }

  const submit = () => {
    if (!name.trim() || !startDate || !endDate) {
      toast.error('Completează nume + perioadă')
      return
    }
    if (channels.length === 0) {
      toast.error('Selectează cel puțin un canal')
      return
    }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error('Data finalului trebuie să fie după data de start')
      return
    }
    onSubmit({
      name: name.trim(),
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      channels,
      productsConfig: [],
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Campanie nouă - pas 1: detalii"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            Creează și continuă →
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Denumire campanie" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Început"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Field
            label="Sfârșit"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Canale</label>
          <div className="flex flex-wrap gap-2">
            {(['boost4life.ro', 'eMAG'] as Channel[]).map((ch) => (
              <label
                key={ch}
                className={`flex items-center gap-2 rounded border px-3 py-1.5 text-sm cursor-pointer ${
                  channels.includes(ch)
                    ? 'border-b4l-green bg-b4l-green-light text-b4l-green-dark'
                    : 'border-b4l-border text-b4l-text-muted'
                }`}
              >
                <input
                  type="checkbox"
                  checked={channels.includes(ch)}
                  onChange={() => toggle(ch)}
                  className="h-4 w-4"
                />
                {ch}
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ============================================================================
// Wizard modal - Steps 2 (products), 3 (P&L), 4 (files)
// ============================================================================

function WizardModal({
  campaignId,
  canWrite,
  onClose,
}: {
  campaignId: number
  canWrite: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<2 | 3 | 4>(2)

  const campaignQuery = useQuery<{ campaign: PromoCampaign }>({
    queryKey: ['promo-campaigns', campaignId],
    queryFn: async () =>
      (await api.get<{ campaign: PromoCampaign }>(`/marketing/promo-campaigns/${campaignId}`)).data,
  })

  const productsQuery = useQuery<{ products: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => (await api.get<{ products: Product[] }>('/products')).data,
    enabled: step === 2,
  })

  const updateMutation = useMutation({
    mutationFn: async (data: { productsConfig: ProductsConfigItem[] }) =>
      (
        await api.patch<{ campaign: PromoCampaign }>(
          `/marketing/promo-campaigns/${campaignId}`,
          data,
        )
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['promo-campaigns', campaignId] })
      toast.success('Salvat')
    },
    onError: showError,
  })

  const calcMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ campaign: PromoCampaign; impact: PAndLImpact }>(
          `/marketing/promo-campaigns/${campaignId}/calculate-pl`,
        )
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['promo-campaigns', campaignId] })
      toast.success('P&L calculat')
    },
    onError: showError,
  })

  const generateMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ campaign: PromoCampaign }>(
          `/marketing/promo-campaigns/${campaignId}/generate-files`,
        )
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['promo-campaigns', campaignId] })
      toast.success('Fișiere generate')
    },
    onError: showError,
  })

  const campaign = campaignQuery.data?.campaign

  return (
    <Modal
      open
      onClose={onClose}
      title={campaign ? `Wizard - ${campaign.name}` : 'Wizard'}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Închide
          </Button>
        </>
      }
    >
      {!campaign ? (
        <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
      ) : (
        <div className="space-y-4">
          <StepIndicator step={step} setStep={setStep} />

          {step === 2 && (
            <StepProducts
              campaign={campaign}
              products={productsQuery.data?.products ?? []}
              canWrite={canWrite}
              loading={productsQuery.isLoading}
              saving={updateMutation.isPending}
              onSave={(productsConfig) => updateMutation.mutate({ productsConfig })}
              onNext={() => setStep(3)}
            />
          )}

          {step === 3 && (
            <StepPnl
              campaign={campaign}
              canWrite={canWrite}
              calcLoading={calcMutation.isPending}
              onCalc={() => calcMutation.mutate()}
              onNext={() => setStep(4)}
            />
          )}

          {step === 4 && (
            <StepFiles
              campaign={campaign}
              canWrite={canWrite}
              generateLoading={generateMutation.isPending}
              onGenerate={() => generateMutation.mutate()}
            />
          )}
        </div>
      )}
    </Modal>
  )
}

function StepIndicator({ step, setStep }: { step: 2 | 3 | 4; setStep: (s: 2 | 3 | 4) => void }) {
  const items: Array<{ id: 2 | 3 | 4; label: string }> = [
    { id: 2, label: '2. Produse + discount' },
    { id: 3, label: '3. P&L impact' },
    { id: 4, label: '4. Generare fișiere' },
  ]
  return (
    <div className="flex gap-2 border-b border-b4l-border pb-2">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => setStep(it.id)}
          className={`rounded px-3 py-1 text-xs ${
            step === it.id
              ? 'bg-b4l-green text-white'
              : 'bg-b4l-bg text-b4l-text-muted hover:bg-b4l-bg/70'
          }`}
        >
          {it.label}
        </button>
      ))}
    </div>
  )
}

// ============================================================================
// Step 2: Products + per-product discount
// ============================================================================

function StepProducts({
  campaign,
  products,
  canWrite,
  loading,
  saving,
  onSave,
  onNext,
}: {
  campaign: PromoCampaign
  products: Product[]
  canWrite: boolean
  loading: boolean
  saving: boolean
  onSave: (config: ProductsConfigItem[]) => void
  onNext: () => void
}) {
  const [items, setItems] = useState<ProductsConfigItem[]>(campaign.productsConfig ?? [])
  const [search, setSearch] = useState('')
  const [defaultDiscount, setDefaultDiscount] = useState(15)

  useEffect(() => {
    setItems(campaign.productsConfig ?? [])
  }, [campaign.productsConfig])

  const itemMap = useMemo(() => new Map(items.map((it) => [it.productId, it.discountPct])), [items])

  const filteredProducts = useMemo(() => {
    const active = products.filter((p) => p.active)
    if (!search.trim()) return active
    const q = search.toLowerCase()
    return active.filter(
      (p) =>
        p.sku.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q),
    )
  }, [products, search])

  const toggle = (productId: number) => {
    setItems((prev) =>
      prev.some((it) => it.productId === productId)
        ? prev.filter((it) => it.productId !== productId)
        : [...prev, { productId, discountPct: defaultDiscount }],
    )
  }

  const setDiscount = (productId: number, pct: number) => {
    setItems((prev) =>
      prev.map((it) => (it.productId === productId ? { ...it, discountPct: pct } : it)),
    )
  }

  const applyDefaultToAll = () => {
    setItems((prev) => prev.map((it) => ({ ...it, discountPct: defaultDiscount })))
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="Caută SKU / nume / brand"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-[260px] flex-1"
        />
        <Field
          label="Discount default %"
          type="number"
          min={0}
          max={99}
          value={String(defaultDiscount)}
          onChange={(e) => setDefaultDiscount(Number(e.target.value) || 0)}
          className="w-32"
        />
        <Button variant="secondary" onClick={applyDefaultToAll} disabled={!canWrite}>
          Aplică tuturor
        </Button>
      </div>

      <div className="text-xs text-b4l-text-muted">
        Selectate: {items.length} / {filteredProducts.length} (filtrate)
      </div>

      <div className="max-h-[380px] overflow-y-auto rounded border border-b4l-border">
        {loading ? (
          <p className="p-4 text-sm text-b4l-text-muted">Se încarcă produsele...</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pl-3 pr-2 font-medium w-10"> </th>
                <th className="py-2 pr-3 font-medium">SKU</th>
                <th className="py-2 pr-3 font-medium">Nume</th>
                <th className="py-2 pr-3 font-medium">Brand</th>
                <th className="py-2 pr-3 font-medium text-right">Discount %</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p) => {
                const checked = itemMap.has(p.id)
                return (
                  <tr
                    key={p.id}
                    className={`border-b border-b4l-border last:border-0 ${
                      checked ? 'bg-b4l-green-light/30' : ''
                    }`}
                  >
                    <td className="py-1.5 pl-3 pr-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(p.id)}
                        disabled={!canWrite}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{p.sku}</td>
                    <td className="py-1.5 pr-3">{p.name}</td>
                    <td className="py-1.5 pr-3 text-xs text-b4l-text-muted">{p.brand}</td>
                    <td className="py-1.5 pr-3 text-right">
                      {checked && (
                        <input
                          type="number"
                          min={0}
                          max={99}
                          value={itemMap.get(p.id) ?? 0}
                          onChange={(e) => setDiscount(p.id, Number(e.target.value) || 0)}
                          disabled={!canWrite}
                          className="w-20 rounded border border-b4l-border px-2 py-0.5 text-right text-xs"
                        />
                      )}
                    </td>
                  </tr>
                )
              })}
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-sm text-b4l-text-muted">
                    Niciun produs găsit
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex justify-end gap-2">
        {canWrite && (
          <Button variant="secondary" onClick={() => onSave(items)} loading={saving}>
            Salvează
          </Button>
        )}
        <Button
          onClick={() => {
            onSave(items)
            onNext()
          }}
          disabled={items.length === 0 || !canWrite}
        >
          Salvează și continuă →
        </Button>
      </div>
    </div>
  )
}

// ============================================================================
// Step 3: P&L impact
// ============================================================================

function StepPnl({
  campaign,
  canWrite,
  calcLoading,
  onCalc,
  onNext,
}: {
  campaign: PromoCampaign
  canWrite: boolean
  calcLoading: boolean
  onCalc: () => void
  onNext: () => void
}) {
  const impact = campaign.pAndLImpact

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-b4l-text-muted">
          Calculul ia în considerare prețul B2C curent per canal, costul de producție și cota TVA.
          Marja se calculează pe preț fără TVA.
        </p>
        {canWrite && (
          <Button onClick={onCalc} loading={calcLoading}>
            {impact ? 'Recalculează' : 'Calculează P&L'}
          </Button>
        )}
      </div>

      {!impact ? (
        <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
          P&L încă nu a fost calculat. Apasă „Calculează P&L”.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <SummaryTile label="Produse incluse" value={impact.totalProducts.toString()} />
            <SummaryTile
              label="Discount mediu"
              value={`${impact.summary.avgDiscountPct.toFixed(1)}%`}
            />
            <SummaryTile
              label="Erodare marjă (RON / unitate)"
              value={impact.summary.totalMarginErosionPerUnit.toFixed(2)}
              tone="danger"
            />
          </div>

          <div className="overflow-x-auto rounded border border-b4l-border">
            <table className="w-full text-xs">
              <thead className="bg-b4l-bg">
                <tr className="text-left uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 px-2 font-medium">SKU</th>
                  <th className="py-2 px-2 font-medium">Produs</th>
                  <th className="py-2 px-2 font-medium">Canal</th>
                  <th className="py-2 px-2 font-medium text-right">Disc %</th>
                  <th className="py-2 px-2 font-medium text-right">Preț bază</th>
                  <th className="py-2 px-2 font-medium text-right">Preț promo</th>
                  <th className="py-2 px-2 font-medium text-right">Cost</th>
                  <th className="py-2 px-2 font-medium text-right">Marjă bază</th>
                  <th className="py-2 px-2 font-medium text-right">Marjă promo</th>
                  <th className="py-2 px-2 font-medium text-right">Pierdere</th>
                </tr>
              </thead>
              <tbody>
                {impact.byProduct.flatMap((bp) =>
                  bp.channels.map((ch) => (
                    <tr
                      key={`${bp.productId}-${ch.channel}`}
                      className="border-t border-b4l-border"
                    >
                      <td className="py-1.5 px-2 font-mono">{bp.sku}</td>
                      <td className="py-1.5 px-2">{bp.name}</td>
                      <td className="py-1.5 px-2 text-b4l-text-muted">{ch.channel}</td>
                      <td className="py-1.5 px-2 text-right">{bp.discountPct}%</td>
                      <td className="py-1.5 px-2 text-right">{ch.basePrice.toFixed(2)}</td>
                      <td className="py-1.5 px-2 text-right">{ch.salePrice.toFixed(2)}</td>
                      <td className="py-1.5 px-2 text-right">
                        {ch.cost == null ? '—' : ch.cost.toFixed(2)}
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        {ch.baseMargin == null
                          ? '—'
                          : `${ch.baseMargin.toFixed(2)} (${ch.baseMarginPct?.toFixed(1)}%)`}
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        {ch.saleMargin == null
                          ? '—'
                          : `${ch.saleMargin.toFixed(2)} (${ch.saleMarginPct?.toFixed(1)}%)`}
                      </td>
                      <td
                        className={`py-1.5 px-2 text-right ${
                          (ch.marginLossPct ?? 0) > 0 ? 'text-b4l-red' : ''
                        }`}
                      >
                        {ch.marginLossPct == null ? '—' : `${ch.marginLossPct.toFixed(1)}p`}
                      </td>
                    </tr>
                  )),
                )}
                {impact.byProduct.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-4 text-center text-b4l-text-muted">
                      Niciun produs cu prețuri configurate
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <Button onClick={onNext}>Continuă spre generare fișiere →</Button>
          </div>
        </>
      )}
    </div>
  )
}

function SummaryTile({ label, value, tone }: { label: string; value: string; tone?: 'danger' }) {
  return (
    <div className="rounded border border-b4l-border bg-white p-3">
      <div className="text-xs text-b4l-text-muted">{label}</div>
      <div
        className={`mt-1 text-lg font-semibold ${tone === 'danger' ? 'text-b4l-red' : 'text-b4l-text'}`}
      >
        {value}
      </div>
    </div>
  )
}

// ============================================================================
// Step 4: Generate + download files
// ============================================================================

function StepFiles({
  campaign,
  canWrite,
  generateLoading,
  onGenerate,
}: {
  campaign: PromoCampaign
  canWrite: boolean
  generateLoading: boolean
  onGenerate: () => void
}) {
  const hasShopify = campaign.channels?.includes('boost4life.ro')
  const hasEmag = campaign.channels?.includes('eMAG')
  const generated =
    campaign.csvShopifyPath != null ||
    campaign.csvRevertPath != null ||
    campaign.xlsEmagPath != null

  const download = async (fileType: 'shopify' | 'shopify-revert' | 'emag') => {
    try {
      const res = await api.get(`/marketing/promo-campaigns/${campaign.id}/download/${fileType}`, {
        responseType: 'blob',
      })
      const blob = res.data as Blob
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download =
        fileType === 'emag'
          ? `campaign-${campaign.id}-emag.xlsx`
          : `campaign-${campaign.id}-${fileType}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      showError(err)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded border border-b4l-border bg-b4l-bg p-3 text-xs text-b4l-text-muted">
        <strong className="text-b4l-text">Important:</strong> fișierele se generează folosind
        prețurile B2C curente din matrice. Dacă ai modificat ceva, recalculează P&L înainte.
        Fișierele de „revert” servesc la întoarcerea la prețurile de bază după încheierea campaniei
        pe Shopify.
      </div>

      {!generated ? (
        canWrite ? (
          <div className="rounded border border-dashed border-b4l-border p-6 text-center">
            <p className="mb-3 text-sm text-b4l-text-muted">
              Apasă „Generează fișiere” pentru a crea CSV/XLSX-urile de upload.
            </p>
            <Button onClick={onGenerate} loading={generateLoading}>
              Generează fișiere
            </Button>
          </div>
        ) : (
          <p className="text-sm text-b4l-text-muted">Fișierele nu au fost generate.</p>
        )
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-end">
            {canWrite && (
              <Button variant="secondary" onClick={onGenerate} loading={generateLoading}>
                Re-generează fișiere
              </Button>
            )}
          </div>

          {hasShopify && (
            <FileRow
              label="Shopify NA Bulk Price Editor (CSV)"
              path={campaign.csvShopifyPath}
              onDownload={() => download('shopify')}
            />
          )}
          {hasShopify && (
            <FileRow
              label="Shopify revert (CSV - prețuri bază, fără strikethrough)"
              path={campaign.csvRevertPath}
              onDownload={() => download('shopify-revert')}
            />
          )}
          {hasEmag && (
            <FileRow
              label="eMAG Prices (XLSX)"
              path={campaign.xlsEmagPath}
              onDownload={() => download('emag')}
            />
          )}
        </div>
      )}
    </div>
  )
}

function FileRow({
  label,
  path,
  onDownload,
}: {
  label: string
  path: string | null
  onDownload: () => void
}) {
  return (
    <div className="flex items-center justify-between rounded border border-b4l-border p-3 text-sm">
      <div>
        <div className="font-medium">{label}</div>
        <div className="mt-0.5 font-mono text-[11px] text-b4l-text-muted">
          {path ? path.split(/[\\/]/).pop() : 'nu există'}
        </div>
      </div>
      <Button variant="secondary" onClick={onDownload} disabled={!path}>
        Descarcă
      </Button>
    </div>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
